/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "PHP SDK", description = "Hightouch's PHP SDK for collecting events", lastUpdatedDate = new Date(1711324800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "PHP SDK",
        description: "Hightouch's PHP SDK for collecting events",
        lastUpdatedDate: "Mon Mar 25 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Installation",
          "depth": 0,
          "id": "installation"
        }, {
          "value": "API",
          "depth": 0,
          "id": "api"
        }, {
          "value": "Identify",
          "depth": 1,
          "id": "identify"
        }, {
          "value": "Track",
          "depth": 1,
          "id": "track"
        }, {
          "value": "Page",
          "depth": 1,
          "id": "page"
        }, {
          "value": "Screen",
          "depth": 1,
          "id": "screen"
        }, {
          "value": "Group",
          "depth": 1,
          "id": "group"
        }, {
          "value": "Flush",
          "depth": 1,
          "id": "flush"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      a: "a",
      pre: "pre",
      code: "code",
      span: "span",
      h3: "h3",
      strong: "strong",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "The PHP SDK makes it easy to track events from PHP applications."
      }), "\n", _jsx(_components.h2, {
        id: "installation",
        children: "Installation"
      }), "\n", _jsxs(_components.p, {
        children: ["The PHP SDK can be installed via ", _jsx(_components.a, {
          href: "https://packagist.org/packages/ht-sdks/events-sdk-php",
          children: "Composer"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-sh",
          children: "composer require ht-sdks/events-sdk-php\n"
        })
      }), "\n", _jsx(_components.p, {
        children: "To initialize the PHP SDK in your application:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "use"
          }), " ", _jsx(_components.span, {
            className: "hljs-title",
            children: "Hightouch"
          }), "\\", _jsx(_components.span, {
            className: "hljs-title",
            children: "Hightouch"
          }), ";\n\n", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Hightouch"
          }), "::", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "init"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'WRITE_KEY'"
          }), ", [\n  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'host'"
          }), " => ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'https://us-east-1.hightouch-events.com'"
          }), ",\n]);\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "api",
        children: "API"
      }), "\n", _jsx(_components.h3, {
        id: "identify",
        children: "Identify"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "identify"
        }), " method sends an ", _jsx(_components.code, {
          children: "identify"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: ["Unlike the ", _jsx(_components.a, {
          href: "./browser",
          children: "Browser SDK"
        }), ", it does ", _jsx(_components.strong, {
          children: "not"
        }), " persist the user ID and\ntraits locally, so user IDs must be explicitly added to other events. This is\nbecause server side events are usually servicing many different users at once."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: [_jsx(_components.span, {
            className: "hljs-title class_",
            children: "Hightouch"
          }), "::", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "identify"
          }), "([\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'userId'"
          }), " => ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'123'"
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'traits'"
          }), " => [\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'location'"
          }), " => ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'San Francisco'"
          }), ",\n    ],\n]);\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's anonymous ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "array"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the user, such as ", _jsx(_components.code, {
                children: "email"
              }), " and ", _jsx(_components.code, {
                children: "name"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "array"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "int"
            }), _jsxs(_components.td, {
              children: ["The UNIX timestamp (seconds) of the message. When backfilling, this can be a time in the past. By default it uses ", _jsx(_components.code, {
                children: "time()"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "track",
        children: "Track"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "track"
        }), " method sends a ", _jsx(_components.code, {
          children: "track"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: [_jsx(_components.span, {
            className: "hljs-title class_",
            children: "Hightouch"
          }), "::", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "track"
          }), "([\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'userId'"
          }), " => ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'123'"
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'event'"
          }), " => ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Order Completed'"
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'properties'"
          }), " => [\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'total'"
          }), " => ", _jsx(_components.span, {
            className: "hljs-number",
            children: "29.99"
          }), ",\n    ],\n]);\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's anonymous ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The name of the event."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "array"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "product_id"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "array"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "int"
            }), _jsxs(_components.td, {
              children: ["The UNIX timestamp (seconds) of the message. When backfilling, this can be a time in the past. By default it uses ", _jsx(_components.code, {
                children: "time()"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "page",
        children: "Page"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "page"
        }), " method sends a ", _jsx(_components.code, {
          children: "page"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: [_jsx(_components.span, {
            className: "hljs-title class_",
            children: "Hightouch"
          }), "::", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "page"
          }), "([\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'anonymousId'"
          }), " => ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'anonymous-id'"
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'name'"
          }), "        => ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'events-sdk-php'"
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'category'"
          }), "    => ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'docs'"
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'properties'"
          }), "  => [\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'path'"
          }), " => ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'/docs/libraries/php/'"
          }), ",\n    ],\n]);\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's anonymous ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "name"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The page's name. For example \"Getting started\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "array"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "url"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "array"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "int"
            }), _jsxs(_components.td, {
              children: ["The UNIX timestamp (seconds) of the message. When backfilling, this can be a time in the past. By default it uses ", _jsx(_components.code, {
                children: "time()"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "screen",
        children: "Screen"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "screen"
        }), " method sends a ", _jsx(_components.code, {
          children: "screen"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: [_jsx(_components.span, {
            className: "hljs-title class_",
            children: "Hightouch"
          }), "::", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "screen"
          }), "([\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'userId'"
          }), "     => ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'123'"
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'name'"
          }), "       => ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Getting started'"
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'properties'"
          }), " => [\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'path'"
          }), " => ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'/getting-started'"
          }), ",\n    ],\n]);\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's anonymous ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "name"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The page's name. For example \"Getting started\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "array"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "url"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "array"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "int"
            }), _jsxs(_components.td, {
              children: ["The UNIX timestamp (seconds) of the message. When backfilling, this can be a time in the past. By default it uses ", _jsx(_components.code, {
                children: "time()"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "group",
        children: "Group"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "group"
        }), " method sends a ", _jsx(_components.code, {
          children: "group"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: [_jsx(_components.span, {
            className: "hljs-title class_",
            children: "Hightouch"
          }), "::", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "group"
          }), "([\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'userId'"
          }), "  => ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'123'"
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'groupId'"
          }), " => ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'G-1'"
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'traits'"
          }), "  => [\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'company_location'"
          }), " => ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'San Francisco'"
          }), ",\n    ],\n]);\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's anonymous ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "groupId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The id for the group."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "array"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the group, such as ", _jsx(_components.code, {
                children: "company_name"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "array"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "int"
            }), _jsxs(_components.td, {
              children: ["The UNIX timestamp (seconds) of the message. When backfilling, this can be a time in the past. By default it uses ", _jsx(_components.code, {
                children: "time()"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "flush",
        children: "Flush"
      }), "\n", _jsx(_components.p, {
        children: "The PHP SDK buffers events locally before sending them to Hightouch's servers.\nThis minimizes the number of requests made by the SDK and improves performance."
      }), "\n", _jsxs(_components.p, {
        children: ["To force the local buffer to be sent to Hightouch immediately call the ", _jsx(_components.code, {
          children: "flush"
        }), "\nmethod. ", _jsx(_components.code, {
          children: "flush"
        }), " should be called when shutting down your app to make sure no events are lost."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: [_jsx(_components.span, {
            className: "hljs-title class_",
            children: "Hightouch"
          }), "::", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "flush"
          }), "();\n"]
        })
      })]
    });
  }
}
export default MDXContent;
